import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
    Users, 
    Building2, 
    FolderKanban, 
    CheckSquare,
    Clock,
    TrendingUp,
    Loader2
} from 'lucide-react';
import api from '../../lib/api';

export default function SuperAdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('/superadmin/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
        );
    }

    const statCards = [
        {
            title: 'Total Users',
            value: stats?.total_users || 0,
            icon: Users,
            color: 'bg-blue-500',
            bgColor: 'bg-blue-500/10'
        },
        {
            title: 'Total Workspaces',
            value: stats?.total_workspaces || 0,
            icon: Building2,
            color: 'bg-purple-500',
            bgColor: 'bg-purple-500/10'
        },
        {
            title: 'Total Spaces',
            value: stats?.total_spaces || 0,
            icon: FolderKanban,
            color: 'bg-emerald-500',
            bgColor: 'bg-emerald-500/10'
        },
        {
            title: 'Total Tasks',
            value: stats?.total_tasks || 0,
            icon: CheckSquare,
            color: 'bg-amber-500',
            bgColor: 'bg-amber-500/10'
        },
        {
            title: 'Completed Tasks',
            value: stats?.completed_tasks || 0,
            icon: TrendingUp,
            color: 'bg-green-500',
            bgColor: 'bg-green-500/10'
        },
        {
            title: 'Active This Week',
            value: stats?.active_users_week || 0,
            icon: Clock,
            color: 'bg-cyan-500',
            bgColor: 'bg-cyan-500/10'
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-white">Dashboard</h1>
                <p className="text-slate-400 mt-1">Statistik sistem TaskGlory</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {statCards.map((stat, index) => (
                    <Card key={index} className="bg-slate-800 border-slate-700">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-slate-400">{stat.title}</p>
                                    <p className="text-3xl font-bold text-white mt-2">{stat.value.toLocaleString()}</p>
                                </div>
                                <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                                    <stat.icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Users */}
                <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Users className="w-5 h-5 text-amber-500" />
                            User Terbaru
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.recent_users?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.recent_users.map((user, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                                        <div>
                                            <p className="text-white font-medium">{user.name}</p>
                                            <p className="text-sm text-slate-400">{user.email}</p>
                                        </div>
                                        <span className="text-xs text-slate-500">
                                            {new Date(user.created_at).toLocaleDateString('id-ID')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-slate-500 text-center py-4">Belum ada user</p>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Workspaces */}
                <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-amber-500" />
                            Workspace Terbaru
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.recent_workspaces?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.recent_workspaces.map((ws, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                                        <div>
                                            <p className="text-white font-medium">{ws.name}</p>
                                            <p className="text-sm text-slate-400">{ws.member_count || 0} members</p>
                                        </div>
                                        <span className="text-xs text-slate-500">
                                            {new Date(ws.created_at).toLocaleDateString('id-ID')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-slate-500 text-center py-4">Belum ada workspace</p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
